// require("es6-promise").polyfill();
import { Module, ActionTree, MutationTree, GetterTree, ActionContext } from "vuex";
import Keycloak, { KeycloakPromise, KeycloakLoginOptions } from "keycloak-js";
import { IUserActivation, UserApiData } from "./models/user-model";
import { userService } from "./service/user.service";
import { loginService } from "./service/login.service";
import { Institution } from "../../models/institution-model";
import store, { type IAuthRoot } from "../../store";
import * as Sentry from "@sentry/browser";

// refs: https://github.com/Anonyfox/vuex-store-module-example
export interface IAuthData {
  authenticated: boolean;
  logoutUrl: string;
  token: string;
  user: UserApiData;
}

let keycloak: Keycloak.KeycloakInstance;
export class AuthDataState implements IAuthData {
  authenticated = false;
  token = "";
  logoutUrl = "";
  user: UserApiData = {} as UserApiData;
}

const namespaced = true;

/**
 *      GETTERS
 */
export const getters: GetterTree<IAuthData, IAuthRoot> = {
  GetToken: (state: IAuthData) => state.token,
  getAuthenticated(): boolean {
    return !!sessionStorage.getItem("token");
  },
  getAuthenticationToken(): string {
    return sessionStorage.getItem("token") || "";
  },
  getKeycloak(): Keycloak.KeycloakInstance | undefined {
    return keycloak;
  },
  getUser: (state: { user: UserApiData }) => () => state.user,
  getInstitutions: (state: { user: UserApiData }) => () => state.user.institutioner,
  getSelectedInstitution: (state: IAuthData) => (): number => {
    return state.user.selected_institution?.id ?? -1;
  }
};

/**
 *      MUTATIONS
 */
export const mutations: MutationTree<IAuthData> = {
  logout(state: IAuthData, userActivation = false): void {
    state.authenticated = false;
    state.token = "";
    sessionStorage.removeItem("token");
    if (!userActivation) {
      history.replaceState(history.state, "", "/");
    }
    if (keycloak) {
      keycloak.logout();
    }
  },
  setAuthenticated(state: IAuthData, authNow: boolean): void {
    state.authenticated = authNow;
  },
  setLogoutUrl(state: IAuthData, url: string): void {
    state.logoutUrl = url;
  },
  setToken(state: IAuthData, token: string): void {
    sessionStorage.setItem("token", token);
    state.token = token;
    state.authenticated = true;
  },
  setUser(state: IAuthData, user: UserApiData): void {
    state.user = user;
    const scope = Sentry.getCurrentScope();
    if (scope) {
      const u: Sentry.User = {};
      u.username = state.user.username;
      scope.setUser(u);
      scope.setTag("institution_id", user?.selected_institution?.id?.toString() || "");
    }
  },
  setSelectedInstitutionId(state: IAuthData, id: number): void {
    state.user.SelectedInstitutionId = id;
    let i = state.user.selectable_institutions?.find(
      (x) => x.id === state.user.SelectedInstitutionId
    );
    if (!i) {
      i = state.user.selectable_institutions?.find(() => true);
    }
    if (i !== undefined) {
      Sentry.getCurrentScope().setTag("institutionskode", i!.code?.toString() || "");
    }
  },
  setInstitutions(state: IAuthData, Institutions: Institution[]): void {
    state.user.institutioner = Institutions;
  },
  setUsername(state: IAuthData, name: string): void {
    state.user.username = name;
  },
  setUserNameAndEmail(state: IAuthData, obj: { name: string; email: string }): void {
    state.user.username = obj.name;
    state.user.email_address = obj.email;
  }
};

/**
 *      ACTIONS
 */
export const actions: ActionTree<IAuthData, IAuthRoot> = {
  async logout({ commit }, userActivation = false): Promise<void> {
    await store.dispatch("reset").then(async () => {
      await loginService
        .Logout()
        .then(() => {
          commit("logout", userActivation);
        })
        .catch(() => {
          commit("logout");
        });
    });
  },
  setLogoutUrl(store: ActionContext<IAuthData, any>, url: string): void {
    store.commit("setLogoutUrl", url);
  },
  getUser(store: ActionContext<IAuthData, any>, update: boolean): Promise<UserApiData | null> {
    return new Promise((resolve, reject) => {
      if (store.state.authenticated && !update) {
        resolve(store.state.user);
      } else {
        userService
          .fetchCurrentUser()
          .then((user: UserApiData) => {
            store.commit("setUser", user);
            store.commit("setSelectedInstitutionId", user.selected_institution?.id);
            resolve(user);
          })
          .catch((error) => {
            reject(error);
          });
      }
    });
  },
  selectInstitution(
    store: ActionContext<IAuthData, any>,
    query: { institutionCode: number; role: number }
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      userService
        .setCurrentInstitution(query.institutionCode, query.role)
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async MarkereNyhedSomLaest(
    store: ActionContext<IAuthData, any>,
    Tekst: string
  ): Promise<boolean> {
    localStorage.setItem("NyhedSomLaest", Tekst);
    return true;
  },
  currentInstitution(store: ActionContext<IAuthData, any>): Institution | undefined {
    let curInstitution: Institution | undefined;
    curInstitution = store.state.user.institutioner.find(
      (i) => i.id === store.state.user.SelectedInstitutionId
    );

    if (!curInstitution) {
      curInstitution = store.state.user.institutioner.find(() => true);
    }
    return curInstitution;
  },
  async isAuthenticated(): Promise<boolean> {
    return new Promise((resolve) => {
      resolve(!!sessionStorage.getItem("token"));
    });
  },
  loginBasic(
    store: ActionContext<IAuthData, any>,
    a: { username: string; password: string }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (store.state.authenticated) {
        resolve(store.state.user);
      } else {
        loginService
          .LoginBasic(a.username, a.password)
          .then((user: UserApiData) => {
            store.commit("setUser", user);
            store.commit("setToken", "loginsession");
            resolve(user);
          })
          .catch((error) => reject(error));
      }
    });
  },

  async loginIdp(authstore: ActionContext<IAuthData, any>): Promise<any> {
    return new Promise((resolve) => {
      resolve(authstore.dispatch("loginIdpByKeycloak"));
    });
  },

  async getIdpToken(authstore: ActionContext<IAuthData, any>): Promise<any> {
    return new Promise((resolve) => {
      resolve(authstore.dispatch("loginIdpByKeycloak", false));
    });
  },

  async loginIdpByKeycloak(authstore: ActionContext<IAuthData, any>, setUser = true): Promise<any> {
    // This really needs to be refactored to get it in two phases
    // 1. It authnticates towards the IDP: keycloak.init
    // 2. It authenticates towards the app server: then((authenticated)
    if (store.state.mainConfig.KEYCLOAK_CLIENTID === "") {
      await store.dispatch("GetConfig");
    }

    return new Promise((resolve, reject) => {
      keycloak = Keycloak({
        realm: store.state.mainConfig.KEYCLOAK_REALM,
        url: store.state.mainConfig.KEYCLOAK_URL,
        clientId: store.state.mainConfig.KEYCLOAK_CLIENTID
      });

      keycloak.onAuthError = (error): void => {
        reject(error);
      };

      // differ on prod non prod environment
      // non prod environment idp (the old one) cannot handle idphint
      const prodEnvironment = ["pre_production", "production"].includes(
        store.state.mainConfig.ENVIRONMENTNAME
      );
      if (prodEnvironment) {
        const kcLogin = keycloak.login;
        keycloak.login = (options: KeycloakLoginOptions): KeycloakPromise<void, void> => {
          options.idpHint = sessionStorage.getItem("idpHint") || "unilogin";
          return kcLogin(options);
        };
      }

      keycloak
        .init({
          onLoad: "login-required",
          redirectUri: authstore.state.logoutUrl,
          checkLoginIframe: false,
          enableLogging: true
        })
        .then(() => {
          if (keycloak.token != undefined) {
            //Tjek om ny bruger
            const jsonString = localStorage.getItem("userActivation");
            if (jsonString !== null) {
              const userActivation: IUserActivation = JSON.parse(jsonString);
              userActivation.accessToken = keycloak.token;
              localStorage.setItem("userActivation", JSON.stringify(userActivation));
            }
            if (setUser) {
              loginService
                .LoginOpenId(keycloak.token)
                .then((user: UserApiData) => {
                  authstore.commit("setUser", user);
                  authstore.commit("setToken", "loginsession");
                  resolve(user);
                })
                .catch((error) => reject(error));
            }
          } else {
            reject("dispatch security/loginIdp, No token received from idp");
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  },

  activateUser(
    authstore: ActionContext<IAuthData, any>,
    obj: {
      ssn: string;
      postalCode: number;
    }
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const jsonString = localStorage.getItem("userActivation");
      if (jsonString !== null) {
        const userActivation: IUserActivation = JSON.parse(jsonString);
        loginService
          .activateNewUser({
            ssn: obj.ssn,
            token: userActivation.mailToken,
            accessToken: userActivation.accessToken,
            postalCode: obj.postalCode
          })
          .then(() => {
            resolve();
          })
          .catch((error: any) => {
            reject(error);
          });
      } else {
        reject();
      }
    });
  },

  idpAuthenticate(authstore: ActionContext<IAuthData, any>): Promise<any> {
    return new Promise((resolve) => {
      resolve(authstore.dispatch("loginIdp"));
    });
  },

  applicationAuthenticate(authstore: ActionContext<IAuthData, any>): Promise<any> {
    return new Promise((resolve) => {
      resolve(authstore.dispatch("loginIdp"));
    });
  },

  sendRecoveryLink(
    store: ActionContext<IAuthData, any>,
    a: { username: string; password: string }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (store.state.authenticated) {
        resolve(store.state.user);
      } else {
        loginService
          .sendRecoveryLink(a.username)
          .then((success: boolean) => {
            resolve(success);
          })
          .catch((error) => reject(error));
      }
    });
  },

  keepAlive(store: ActionContext<IAuthData, any>): void {
    loginService
      .KeepAlive()
      .then(() => {
        // SUCCESS, DO NOTHING
      })
      .catch(() => {
        history.replaceState(history.state, "", "/");
        store.commit("security/logout");
      });
  }
};

export const securityStoreModule: Module<IAuthData, IAuthRoot> = {
  namespaced: namespaced,
  state: new AuthDataState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};

window.setInterval(() => {
  if (keycloak !== undefined) {
    // console.log(
    //   "Before refresh",
    //   keycloakAuth.tokenParsed,
    //   keycloakAuth.authenticated
    // );
    const token: string | null = sessionStorage.getItem("token");
    if (token) {
      keycloak
        .updateToken(70)
        .then((refreshed: any) => {
          if (refreshed) {
            sessionStorage.setItem("token", keycloak.token || "");
            // console.log("Refreshed token");
          } else {
            // console.log("Not refreshed token");
          }
        })

        .catch(() => {
          // console.error("Failed to refresh token");
          store.dispatch("security/loginIdp", {
            path: document.location.pathname
          });
        });
    }
  }
}, 6000);
